import React, { useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { AddCircle, Delete } from '@mui/icons-material';

export interface AvailableItem {
  id: number;
  name: string;
}

export interface InboundOrderItem {
  item_id: number;
  item_name: string;
  quantity: number;
  note: string;
}

interface InboundItemsTableProps {
  items: InboundOrderItem[];
  availableItems: AvailableItem[];
  onItemsChange: (items: InboundOrderItem[]) => void;
}

const InboundItemsTable: React.FC<InboundItemsTableProps> = ({
  items,
  availableItems,
  onItemsChange,
}) => {
  // Create refs for quantity input fields
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const handleItemSelect = (index: number, selectedItem: AvailableItem | null) => {
    const newItems = [...items];

    if (selectedItem) {
      newItems[index] = {
        ...newItems[index],
        item_id: selectedItem.id,
        item_name: selectedItem.name,
      };
      onItemsChange(newItems);
      
      // Focus on Qty field after item selection
      setTimeout(() => {
        const qtyRef = inputRefs.current[`${index}-qty`];
        if (qtyRef) qtyRef.focus();
      }, 50);
      return;
    } else {
      newItems[index] = {
        item_id: 0,
        item_name: '',
        quantity: 0,
        note: '',
      };
    }
    onItemsChange(newItems);
  };

  const handleQuantityChange = (index: number, value: string) => {
    const newItems = [...items];
    
    // Allow only digits
    const onlyDigits = value.replace(/[^\d]/g, '');
    const numValue = onlyDigits === '' ? 0 : parseInt(onlyDigits, 10);

    if (isNaN(numValue) || numValue < 0) return;

    newItems[index] = { ...newItems[index], quantity: numValue };
    onItemsChange(newItems);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, currentIndex: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();

      const nextIndex = currentIndex + 1;
      const nextRowHasItem = items[nextIndex]?.item_id > 0;
      const nextRef = nextRowHasItem
        ? inputRefs.current[`${nextIndex}-qty`]
        : inputRefs.current[`${nextIndex}-itemSearch`];

      if (nextRef) {
        nextRef.focus();
      } else {
        addItemRow();
        setTimeout(() => {
          const newRef = inputRefs.current[`${nextIndex}-itemSearch`];
          if (newRef) newRef.focus();
        }, 50);
      }
    }
  };

  const addItemRow = () => {
    onItemsChange([
      ...items,
      {
        item_id: 0,
        item_name: '',
        quantity: 0,
        note: '',
      },
    ]);
  };

  const removeItemRow = (index: number) => {
    onItemsChange(items.filter((_, i) => i !== index));
  };

  const getSelectedItem = (item: InboundOrderItem): AvailableItem | null => {
    if (item.item_id === 0) return null;
    return availableItems.find((i) => i.id === item.item_id) || null;
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Items
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5, width: '100%' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold', width: '45%' }}>Item</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '20%' }} align="center">Qty</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '25%' }} align="center">Note</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={availableItems}
                    getOptionLabel={(option) => option.name}
                    value={getSelectedItem(item)}
                    onChange={(_, newValue) => handleItemSelect(index, newValue)}
                    filterOptions={(options, { inputValue }) => {
                      const filterValue = inputValue.toLowerCase();
                      return options.filter((option) =>
                        option.name.toLowerCase().includes(filterValue)
                      );
                    }}
                    ListboxProps={{
                      style: { maxHeight: 200 },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search item..."
                        size="small"
                        inputRef={(ref) => {
                          if (ref) inputRefs.current[`${index}-itemSearch`] = ref;
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Typography variant="body2">{option.name}</Typography>
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    size="small"
                    type="text"
                    inputMode="numeric"
                    value={item.quantity || ''}
                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, index)}
                    inputProps={{
                      style: { textAlign: 'center' },
                    }}
                    placeholder="0"
                    sx={{ width: 80 }}
                    inputRef={(el) => { inputRefs.current[`${index}-qty`] = el; }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    size="small"
                    value={item.note || ''}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[index] = { ...newItems[index], note: e.target.value };
                      onItemsChange(newItems);
                    }}
                    disabled={item.item_id === 0}
                    placeholder="Note"
                    inputProps={{ style: { textAlign: 'center' } }}
                  />
                </TableCell>
                <TableCell align="center">
                  {items.length > 1 && (
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => removeItemRow(index)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button startIcon={<AddCircle />} onClick={addItemRow} size="small">
          Add Item
        </Button>
      </Box>
    </Box>
  );
};

export default InboundItemsTable;
